<template>
  <div>
    <h2 class="content-block">
      Automatic Fees for {{ comparisonSourceName }} {{ currencyName }}
    </h2>
    <div class="content-block">
      <h3>Existing Schemes</h3>

      <dx-data-grid
        :data-source="existingFeeStructures"
        :show-borders="true"
        key-expr="id"
        :remote-operations="false"
        :allow-column-reordering="false"
        :allow-column-resizing="true"
        :row-alternation-enabled="true"
        :hover-state-enabled="true"
        :word-wrap-enabled="true"
        :column-auto-width="true"
        :show-row-lines="true"
        :show-column-lines="true"
        class="dx-card wide-card"
        width="100%"
      >
        <dx-export
          :enabled="true"
          :file-name="
            `${currentDate}-fee-schemes-${comparisonSourceName}-${currencyName}`
          "
        />
        <dx-paging :page-size="pageSizes[0]" />
        <dx-pager
          :allowed-page-sizes="pageSizes"
          :show-page-size-selector="true"
          :show-navigation-buttons="true"
          :show-info="true"
        />
        <dx-filter-row :visible="true" />
        <dx-header-filter :visible="false" />
        <dx-filter-panel :visible="true" />
        <dx-column data-field="id" caption="Id" :format="config.idFormat" />
        <dx-column
          data-field="from"
          data-type="datetime"
          :format="config.intlDateTimeFormat"
          :caption="'From (' + getUserTimeZone() + ')'"
        />
        <dx-column
          data-field="to"
          data-type="datetime"
          :format="config.intlDateTimeFormat"
          :caption="'To (' + getUserTimeZone() + ')'"
        />
        <dx-column
          data-type="datetime"
          :caption="'From (' + getGatewayTimeZoneName() + ')'"
          cell-template="from-time-cell-template"
        />
        <template #from-time-cell-template="{data}">
          <div>
            {{ formatTime(data.data.from, getGatewayTimeZoneName(), false) }}
          </div>
        </template>
        <dx-column
          data-type="datetime"
          :caption="'To (' + getGatewayTimeZoneName() + ')'"
          cell-template="to-time-cell-template"
        />
        <template #to-time-cell-template="{data}">
          <div>
            {{ formatTime(data.data.to, getGatewayTimeZoneName(), false) }}
          </div>
        </template>
        <dx-column
          data-field="created"
          data-type="datetime"
          :format="config.intlDateTimeFormat"
          caption="Created"
        />
        <dx-column
          data-field="userIdCreated"
          :lookup="userColumnLookup"
          caption="Created by"
        />
        <dx-column
          data-field="processed"
          data-type="datetime"
          :format="config.intlDateTimeFormat"
          caption="Processed"
        />
        <dx-column
          data-field="deleted"
          data-type="datetime"
          :format="config.intlDateTimeFormat"
          caption="Deleted"
        />
        <dx-column
          data-field="userIdDeleted"
          :lookup="userColumnLookup"
          caption="Deleted by"
        />
        <dx-column
          data-field="feeTiers.length"
          caption="Tiers"
          :format="config.countFormat"
        />
        <dx-column
          caption="Actions"
          cell-template="button-cell-template"
          :allow-sorting="false"
        />
        <template #button-cell-template="{data}">
          <div>
            <dx-button
              icon="rowfield"
              text="View tiers"
              @click="showTiers(data.data)"
            />
            <dx-button
              :disabled="data.data.deleted !== null"
              icon="trash"
              text="Delete"
              @click="deleteScheme(data.data.id)"
            />
            <dx-button
              :disabled="data.data.deleted !== null"
              icon="clock"
              text='Change "To"'
              @click="changeTo(data.data)"
            />
          </div>
        </template>
      </dx-data-grid>
    </div>
    <div class="content-block">
      <h3>Create New Fee Scheme</h3>
      <div class="dx-fieldset">
        <div class="dx-field">
          <date-range :date-range="dateRange" @dateUpdate="dateUpdate" />
        </div>
        <div class="dx-field">
          <div class="dx-field-label">Number of tiers:</div>
          <div class="dx-field-value">
            <DxDropDownBox
              :value.sync="tierCount"
              :opened.sync="isTierCountDropDownBoxOpened"
              styling-mode="outlined"
            >
              <DxList
                :data-source="tierNumberList"
                selection-mode="single"
                @selection-changed="changeTierNumberValue"
              />
            </DxDropDownBox>
          </div>
        </div>
        <div v-for="tier in tierCount" :key="tier">
          <div class="dx-fieldset-header">Tier {{ tier }}</div>
          <div class="dx-field">
            <div class="dx-field-label">
              Start amount (inclusive):
            </div>
            <div class="dx-field-value">
              <DxNumberBox
                ref="start-amount-value"
                :show-clear-button="true"
                :min="0"
                placeholder="Enter start amount"
                styling-mode="outlined"
              />
            </div>
          </div>
          <div class="dx-field">
            <div class="dx-field-label">
              Flat fee per transaction:
            </div>
            <div class="dx-field-value">
              <DxNumberBox
                ref="flat-value"
                :show-clear-button="true"
                :min="0"
                placeholder="Enter flat fee"
                :value="0"
                styling-mode="outlined"
              />
            </div>
          </div>
          <div class="dx-field">
            <div class="dx-field-label">
              Multiplier on transaction value:
            </div>
            <div class="dx-field-value">
              <DxNumberBox
                ref="multiplier-value"
                :show-clear-button="true"
                :min="0"
                placeholder="Enter multiplier"
                :value="0"
                styling-mode="outlined"
              />
            </div>
          </div>
        </div>

        <div class="dx-field">
          <div class="dx-field-label">
            <DxButton
              :disabled="savingScheme"
              :width="200"
              text="Create Fee Scheme"
              icon="floppy"
              @click="saveTier"
            />
          </div>
        </div>
      </div>
    </div>
    <DxPopup
      :visible.sync="feeTierPopupVisible"
      :drag-enabled="true"
      :close-on-outside-click="true"
      :show-title="true"
      :width="780"
      :height="650"
      :title="'Tiers for #' + feeTierPopupScheme.id"
    >
      <div>
        <dx-data-grid
          :data-source="feeTierPopupScheme.feeTiers"
          :show-borders="true"
          key-expr="id"
          :remote-operations="false"
          :allow-column-reordering="false"
          :allow-column-resizing="true"
          :row-alternation-enabled="true"
          :hover-state-enabled="true"
          :word-wrap-enabled="true"
          :column-auto-width="true"
          :show-row-lines="true"
          :show-column-lines="true"
          class="dx-card wide-card"
          width="100%"
        >
          <dx-column-chooser :enabled="false" />
          <dx-export
            :enabled="true"
            :file-name="`${currentDate}-tiers-${feeTierPopupScheme.id}`"
          />
          <dx-column data-field="id" />
          <dx-column
            data-field="startAmount"
            :format="config.financialNumberFormat"
            caption="Starts from"
          />
          <dx-column
            data-field="flat"
            :format="config.financialNumberFormat"
            caption="Flat"
          />
          <dx-column
            data-field="multiplier"
            format="fixedPoint"
            caption="Multiplier"
          />
        </dx-data-grid>
      </div>
    </DxPopup>
    <DxPopup
      :visible.sync="changeToPopupVisible"
      :drag-enabled="true"
      :close-on-outside-click="true"
      :show-title="true"
      :height="150"
      :width="250"
      :title="'Change &quot;To&quot; date for  #' + changeToFeeStructureId"
    >
      <div>
        <DxDateBox
          :value.sync="changeToTime"
          type="datetime"
          :display-format="config.dxDateTimeFormat"
          :min="changeToMin"
          :max="changeToMax"
        />
        <DxButton
          class="save-to-date-button"
          :width="200"
          text='Save "To" date'
          icon="floppy"
          @click="saveToDate"
        />
      </div>
    </DxPopup>
  </div>
</template>
<script>
	import {DxDateBox, DxDropDownBox, DxTagBox} from 'devextreme-vue'
	import {show} from '@/utils/show'
	import {DxPopup} from 'devextreme-vue/popup'
	import {mapState} from 'vuex'
	import {
		DxColumn,
		DxColumnChooser,
		DxDataGrid,
		DxExport,
		DxFilterPanel,
		DxFilterRow,
		DxHeaderFilter,
		DxPager,
		DxPaging,
	} from 'devextreme-vue/data-grid'
	import {DxButton} from 'devextreme-vue/button'
	import {http} from '@/utils/http'
	import DateRange from '@/components/date-range'
	import {config} from '@/config'
	import DxList from 'devextreme-vue/list'
	import {DxNumberBox} from 'devextreme-vue/number-box'
	import {showHttpError} from '../../utils/http'
	import {formatTime, getUserTimeZone} from '../../utils/time'

	export default {
  name: 'ViewStatement',
  components: {
    DateRange,
    DxColumn,
    DxColumnChooser,
    DxDataGrid,
    DxExport,
    DxFilterRow,
    DxFilterPanel,
    DxHeaderFilter,
    DxPager,
    DxPaging,
    DxButton,
    // eslint-disable-next-line vue/no-unused-components
    DxTagBox,
    DxList,
    DxDropDownBox,
    DxNumberBox,
    DxPopup,
    DxDateBox,
  },
  data() {
    return {
      gateway: this.$route.params.gateway,
      source: this.$route.params.source,
      currencyId: this.$route.params.currencyId,
      config,
      dataSource: null,

      pageSizes: config.itemsPerPage,
      existingFeeStructures: null,

      loading: false,

      comparisonSourceName: null,
      currencyName: null,

      dateRange: {
        startDate: new Date(),
        endDate: new Date(new Date().getTime() - config.oneDay * 365),
      },

      tierCount: 3,
      isTierCountDropDownBoxOpened: false,
      tierNumberList: Array.from(Array(20), (value, index) => index + 1),

      savingScheme: false,

      feeTierPopupVisible: false,
      feeTierPopupScheme: {
        id: null,
        feeTiers: [],
      },

      changeToPopupVisible: false,
      changeToFeeStructureId: null,
      changeToTime: null,
      changeToMin: null,
      changeToMax: null,
    }
  },
  computed: {
    ...mapState('user', [
      'userColumnLookup',
      'gatewayLookup',
      'currencyLookup',
    ]),
    currentDate() {
      return new Date().toISOString().split('T')[0]
    },
  },
  created() {
    this.comparisonSourceName = this.gatewayLookup[this.gateway].sources[this.source].name
    this.currencyName = this.currencyLookup[this.currencyId].iso
    this.loadData()
  },
  methods: {
    getUserTimeZone,
    formatTime,
    async loadData() {
      const response = await http
        .get(
          'reconciliation/admin/fee/byComparisonSource/' +
            this.gateway + '/' +
            this.source + '/' +
            this.currencyId,
        )
        .json()
      this.existingFeeStructures = response.feeStructures
    },
    dateUpdate(dateRange) {
      this.dateRange = Object.assign({}, this.dateRange, dateRange)
    },
    changeTierNumberValue(e) {
      this.tierCount = e.addedItems[0]
      this.isTierCountDropDownBoxOpened = false
    },
    saveTier() {
      this.savingScheme = true
      http
        .post('reconciliation/admin/fee/save', {
          json: {
            gateway: this.gateway,
            source: this.source,
            currencyId: this.currencyId,
            from: this.dateRange.startDate.getTime(),
            to: this.dateRange.endDate.getTime(),
            feeTiers: Array.from(Array(this.tierCount), (value, index) => ({
              startAmount: this.$refs['start-amount-value'][
                index
              ].instance.option('value'),
              multiplier: this.$refs['multiplier-value'][index].instance.option(
                'value',
              ),
              flat: this.$refs['flat-value'][index].instance.option('value'),
            })),
          },
        })
        .then(() => {
          show.success(
            'Fee structure is being created. When is completed the Processed timestamp will be set.',
          )
        })
        .catch((e) => {
          showHttpError(e)
          this.savingScheme = false
        })
    },
    showTiers(feeScheme) {
      this.feeTierPopupVisible = true
      this.feeTierPopupScheme = feeScheme
    },
    deleteScheme(feeSchemeId) {
      http
        .get('reconciliation/admin/fee/delete/' + feeSchemeId)
        .then(() => {
          show.success(
            'Fee structure sent for deletion. When it is completed the deleted timestamp will be set.',
          )
        })
        .catch((e) => {
          showHttpError(e)
        })
    },
    getGatewayTimeZoneName() {
      return this.gatewayLookup[this.gateway].timeZone
    },
    changeTo(feeStructure) {
      this.changeToPopupVisible = true
      this.changeToFeeStructureId = feeStructure.id
      this.changeToMin = feeStructure.from
      this.changeToMax = this.changeToTime = feeStructure.to
    },
    saveToDate() {
      this.changeToPopupVisible = false
      http
        .post('reconciliation/admin/fee/changeToDate', {
          json: {
            feeStructureId: this.changeToFeeStructureId,
            to: this.changeToTime,
          },
        })
        .then(() => {
          show.success(
            'The "To" date is being changed. The "To" column will be updated when the process has finished. Do not make any further fee scheme changes until the process is completed.',
          )
        })
        .catch((e) => {
          showHttpError(e)
        })
    },
  },
}
</script>
<style lang="scss">
td[role='columnheader'] {
  text-align: center !important;
}

.dx-button {
  float: left !important;
}
.save-to-date-button {
  margin-top: 10px;
}
</style>
